import React, { useState, useEffect, useContext } from 'react';
import { connect } from 'react-redux';
import {
  KFLInput,
  KFLCheckbox,
  KFLDropdown,
  KFLDropdownItem,
  KFLRadioButton,
} from '@kamstrup/kamstrup-frontend-library';
import { ReduxState } from '../../interfaces';
import { updateFilterCriteria } from '../../modules/devices/actions';
import { useLifecycle } from '../../hooks/useLifecycle';
import * as Devices from '../../modules/devices';
import SidebarForm from './sidebarForm';
import { Groups } from '../../models';
import { resetScroll } from '../../modules/datatable';
import { SettingsCtx } from '../../contexts/settings.context';
import { LocaleCtx } from '../../contexts/locale.context';
import LinkText from '../shared/linkText';
import EksKFLWrapper from '../shared/eks-lib/eksKFLwrapper';

interface StateProps {
  filterCriteria: Devices.FilterCritria;
  isLoading: boolean;
}

type Props = StateProps & { id: string };

const DeviceSidebar: React.FC<Props> = (props) => {
  useLifecycle('DeviceSidebar');
  const { settings } = useContext(SettingsCtx);
  const products = ['', ...(settings?.products ?? [])];

  const [isFilterById, setIsFilterById] = useState(true);

  const [serialNumber, setSerialNumber] = useState(
    props.filterCriteria.serialNumber
  );
  const [snLabel, setSnLabel] = useState(props.filterCriteria.snLabel);
  const [orderNumber, setOrderNumber] = useState(
    props.filterCriteria.orderNumber
  );
  const [deviceName, setDeviceName] = useState(props.filterCriteria.deviceName);
  const [owner, setOwner] = useState(props.filterCriteria.owner);
  const [userWithReadRights, setUserWithReadRights] = useState(
    props.filterCriteria.userWithReadRights
  );
  const [includeRemoved, setIncludeRemoved] = useState(
    Devices.includeRemoved(props.filterCriteria.deviceStatus)
  );
  const [includeActive, setIncludeActive] = useState(
    Devices.includeActives(props.filterCriteria.deviceStatus)
  );

  useEffect(() => {
    setSerialNumber(props.filterCriteria.serialNumber);
  }, [props.filterCriteria.serialNumber]);

  const handleFilterByIdChanged = (value: boolean) =>
    isFilterById !== value && setIsFilterById(value);
  const handleSerialNumberChanged = (value: any) => setSerialNumber(value);
  const handleSNLabelChanged = (value: any) => setSnLabel(value);
  const handleOrderNumberChanged = (value: string) => setOrderNumber(value);
  const handleProductChanged = (value: string) => setDeviceName(value);
  const handleOwnerChanged = (value: string) => setOwner(value);
  const handleReadRightsChanged = (value: string) =>
    setUserWithReadRights(value);

  const handleCheckboxActiveClicked = () => setIncludeActive(!includeActive);
  const handleCheckboxRemovedClicked = () => setIncludeRemoved(!includeRemoved);
  const handleSearch = () => {
    const criteria = {
      ...props.filterCriteria,
      serialNumber: isFilterById ? serialNumber : '',
      snLabel: isFilterById ? '' : snLabel,
      orderNumber,
      deviceName,
      owner,
      userWithReadRights,
      deviceStatus: Devices.getStatus(includeActive, includeRemoved),
    };
    updateFilterCriteria(criteria, props.isLoading);
    resetScroll();
  };
  const handleClear = () => {
    setSerialNumber('');
    setSnLabel('');
    setOrderNumber('');
    setDeviceName('');
    setOwner('');
    setUserWithReadRights('');
    setIncludeRemoved(false);
    setIncludeActive(true);
  };

  const { translate } = useContext(LocaleCtx);
  const kamstrupIdTranslated = translate(Devices.msgKId);
  const serialnumberTranslated = translate(Devices.msgSN);
  const orderNumberTranslated = translate(Devices.msgOrder);
  const deviceNameTranslated = translate(Devices.msgDeviceName);
  const ownerTranslated = translate(Devices.msgOwner);
  const readrightsTranslated = translate(Devices.msgReadRights);
  const searchViaFileTranslated = translate(Devices.msgSearchViaFileBtn);

  return (
    <SidebarForm
      group={Groups.CUSTOMERS}
      fnSearch={handleSearch}
      fnClear={handleClear}
      isLoading={props.isLoading}
    >
      <div style={{ display: 'flex' }}>
        <KFLRadioButton
          id="sb_devices_filter_id_snlabel"
          checked={!isFilterById}
          onChange={() => handleFilterByIdChanged(false)}
        >
          {serialnumberTranslated}
        </KFLRadioButton>
        <KFLRadioButton
          id="sb_devices_filter_id_kid"
          checked={isFilterById}
          onChange={() => handleFilterByIdChanged(true)}
        >
          {kamstrupIdTranslated}
        </KFLRadioButton>
      </div>
      {isFilterById ? (
        <>
          <KFLInput
            id="sb_devices_filter_id"
            label={kamstrupIdTranslated}
            placeholder="E.g. KAM12345678"
            value={String(serialNumber)}
            onChange={handleSerialNumberChanged}
          />
          <div style={{ textAlign: 'right' }}>
            <LinkText
              onClick={Devices.openSearchViaFile}
              content={searchViaFileTranslated}
            />
          </div>
        </>
      ) : (
        <>
          <KFLInput
            id="sb_snLabels_filter_id"
            label={serialnumberTranslated}
            placeholder="E.g. DEVICELABEL1234"
            value={String(snLabel)}
            onChange={handleSNLabelChanged}
          />
        </>
      )}

      <KFLInput
        id="sb_devices_filter_order"
        label={orderNumberTranslated}
        placeholder={orderNumberTranslated}
        value={orderNumber}
        onChange={handleOrderNumberChanged}
      />

      <div className="filter_label">{deviceNameTranslated}</div>
      <EksKFLWrapper darkMode>
        <KFLDropdown key="sb_devices_filter_product" fullWidth>
          {products.map((i) => (
            <KFLDropdownItem
              key={`sb_devices_filter_product_${i}`}
              onClick={() => handleProductChanged(i)}
              selected={(deviceName ?? '') === (i ?? '') ? true : false}
            >
              {i ? i : 'Any'}
            </KFLDropdownItem>
          ))}
        </KFLDropdown>
      </EksKFLWrapper>

      <KFLInput
        id="sb_devices_filter_owner"
        label={ownerTranslated}
        placeholder={ownerTranslated}
        value={owner}
        onChange={handleOwnerChanged}
      />
      <KFLInput
        id="sb_devices_filter_readrights"
        label={readrightsTranslated}
        placeholder={readrightsTranslated}
        value={userWithReadRights}
        onChange={handleReadRightsChanged}
      />
      <div className="filter_label">{translate(Devices.msgStatus)}</div>
      <div className="checkbox-filter">
        <EksKFLWrapper>
          <KFLCheckbox
            id="sb_devices_filter_status_active"
            checked={includeActive}
            onChange={handleCheckboxActiveClicked}
          />
        </EksKFLWrapper>
        <span className="label" onClick={handleCheckboxActiveClicked}>
          {translate(Devices.msgStatusActive)}
        </span>
      </div>

      <div className="checkbox-filter">
        <EksKFLWrapper>
          <KFLCheckbox
            id="sb_devices_filter_status_removed"
            checked={includeRemoved}
            onChange={handleCheckboxRemovedClicked}
          />
        </EksKFLWrapper>
        <span className="label" onClick={handleCheckboxRemovedClicked}>
          {translate(Devices.msgStatusRemoved)}
        </span>
      </div>
    </SidebarForm>
  );
};

const mapStateToProps = (state: ReduxState) => ({
  isLoading: state.devicesState.table.isLoading,
  filterCriteria: state.devicesState.filters,
});

export default connect(mapStateToProps)(DeviceSidebar);
